import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import type { BattleManager } from "../../game/assets/pri_wqs/scripts/core/BattleManager";
import type { PropRuntimeEvent } from "../../game/assets/pri_wqs/scripts/core/propMechanics/PropMechanicsTypes";

interface FloatingMagicText {
  id: number;
  text: string;
  x: number;
  y: number;
}

interface MagicEffectTask {
  itemId: string;
  text: string;
}

interface MagicEffectAnchorProps {
  itemId?: string;
  disabled?: boolean;
  children: ReactNode;
}

interface MagicGainEffectsProps {
  manager?: BattleManager;
  onPlayingChange?: (playing: boolean) => void;
}

const anchorRegistry = new Map<string, Set<HTMLElement>>();
let floatingTextSeed = 0;
const MAGIC_FLOAT_DURATION_MS = 820;
const MAGIC_FLOAT_START_INTERVAL_MS = 100;
const magicGainWaiters = new Set<() => void>();
let magicGainEffectPlaying = false;

export function isMagicGainEffectPlaying(): boolean {
  return magicGainEffectPlaying;
}

export function waitForMagicGainEffects(timeoutMs?: number): Promise<void> {
  if (!magicGainEffectPlaying) return Promise.resolve();
  return new Promise((resolve) => {
    let timer: number | undefined;
    const finish = () => {
      if (timer !== undefined) window.clearTimeout(timer);
      magicGainWaiters.delete(finish);
      resolve();
    };
    magicGainWaiters.add(finish);
    if (typeof timeoutMs === "number" && timeoutMs > 0) {
      timer = window.setTimeout(finish, timeoutMs);
    }
  });
}

/**
 * 注册一个道具实例的魔力飘字锚点。
 *
 * 同一个实例可能同时出现在盘面和原料库中，因此注册表允许一个 id 对应多个 DOM。
 * 播放动画时会优先选择仍然挂载且有可见尺寸的第一个锚点。
 */
export function MagicEffectAnchor({ itemId, disabled, children }: MagicEffectAnchorProps) {
  const anchorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const element = anchorRef.current;
    if (!itemId || disabled || !element) return undefined;
    registerMagicEffectAnchor(itemId, element);
    return () => unregisterMagicEffectAnchor(itemId, element);
  }, [disabled, itemId]);

  return <MagicAnchor ref={anchorRef}>{children}</MagicAnchor>;
}

/**
 * 监听魔力变化事件并播放沙盒表现效果。
 *
 * 该组件只负责表现层动画：
 * - 根据 `magicChanged.targetIds` 寻找目标道具卡片锚点；
 * - 按顺序播放 `+n` 飘字；
 * - 自动清理已经结束的飘字状态。
 */
export function MagicGainEffects({ manager, onPlayingChange }: MagicGainEffectsProps) {
  const [texts, setTexts] = useState<FloatingMagicText[]>([]);
  const timersRef = useRef<number[]>([]);
  const queueRef = useRef<MagicEffectTask[]>([]);
  const activeCountRef = useRef(0);
  const schedulerRef = useRef<number | undefined>(undefined);
  const playingRef = useRef(false);
  const onPlayingChangeRef = useRef(onPlayingChange);

  useEffect(() => {
    onPlayingChangeRef.current = onPlayingChange;
  }, [onPlayingChange]);

  useEffect(() => {
    const notifyPlaying = (playing: boolean) => {
      if (playingRef.current === playing) return;
      playingRef.current = playing;
      setGlobalMagicGainPlaying(playing);
      onPlayingChangeRef.current?.(playing);
    };

    const finishIfIdle = () => {
      if (queueRef.current.length || activeCountRef.current > 0 || schedulerRef.current !== undefined) return;
      notifyPlaying(false);
    };

    const playOne = () => {
      const task = queueRef.current.shift();
      if (!task) {
        finishIfIdle();
        return;
      }

      const anchor = findMagicEffectAnchor(task.itemId);
      if (!anchor) {
        playOne();
        return;
      }

      const rect = anchor.getBoundingClientRect();
      const text: FloatingMagicText = {
        id: ++floatingTextSeed,
        text: task.text,
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height * 0.34,
      };

      activeCountRef.current += 1;
      notifyPlaying(true);
      setTexts((previous) => [...previous, text]);

      const removeTimer = window.setTimeout(() => {
        setTexts((previous) => previous.filter((item) => item.id !== text.id));
        activeCountRef.current = Math.max(0, activeCountRef.current - 1);
        finishIfIdle();
      }, MAGIC_FLOAT_DURATION_MS);
      timersRef.current.push(removeTimer);
    };

    const startScheduler = () => {
      if (schedulerRef.current !== undefined) return;
      notifyPlaying(true);
      schedulerRef.current = window.setInterval(() => {
        if (!queueRef.current.length) {
          if (schedulerRef.current !== undefined) window.clearInterval(schedulerRef.current);
          schedulerRef.current = undefined;
          finishIfIdle();
          return;
        }
        playOne();
      }, MAGIC_FLOAT_START_INTERVAL_MS);
      playOne();
    };

    timersRef.current.forEach((timer) => window.clearTimeout(timer));
    if (schedulerRef.current !== undefined) window.clearInterval(schedulerRef.current);
    timersRef.current = [];
    queueRef.current = [];
    activeCountRef.current = 0;
    schedulerRef.current = undefined;
    notifyPlaying(false);
    setTexts([]);
    if (!manager) return undefined;

    const unsubscribe = manager.getEventBus().on("magicChanged", (event) => {
      const delta = Number(event.magicDelta ?? 0);
      if (delta <= 0) return;
      const anchorIds = readMagicAnchorIds(event).filter((itemId) => Boolean(findMagicEffectAnchor(itemId)));
      if (!anchorIds.length) return;

      queueRef.current.push(...anchorIds.map((itemId) => ({
        itemId,
        text: `+${formatMagicDelta(delta)}`,
      })));
      startScheduler();
    });

    return () => {
      unsubscribe();
      timersRef.current.forEach((timer) => window.clearTimeout(timer));
      if (schedulerRef.current !== undefined) window.clearInterval(schedulerRef.current);
      timersRef.current = [];
      queueRef.current = [];
      activeCountRef.current = 0;
      schedulerRef.current = undefined;
      notifyPlaying(false);
    };
  }, [manager]);

  return (
    <FloatingLayer>
      {texts.map((item) => (
        <FloatingText key={item.id} style={{ left: item.x, top: item.y }}>
          {item.text}
        </FloatingText>
      ))}
    </FloatingLayer>
  );
}

/**
 * 将数值变化平滑过渡到目标值。
 *
 * 新目标在旧动画未结束时到来，会从当前显示值继续过渡，避免闪烁。
 */
export function useAnimatedNumber(value: number, duration = 420): number {
  const [displayValue, setDisplayValue] = useState(value);
  const frameRef = useRef<number | undefined>(undefined);
  const latestDisplayRef = useRef(value);

  useEffect(() => {
    if (frameRef.current !== undefined) window.cancelAnimationFrame(frameRef.current);
    const from = latestDisplayRef.current;
    const to = Number(value || 0);
    if (Object.is(from, to)) {
      setDisplayValue(to);
      return undefined;
    }
    const startedAt = performance.now();
    const tick = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      const next = from + (to - from) * eased;
      latestDisplayRef.current = next;
      setDisplayValue(next);
      if (progress < 1) frameRef.current = window.requestAnimationFrame(tick);
      else {
        latestDisplayRef.current = to;
        setDisplayValue(to);
      }
    };
    frameRef.current = window.requestAnimationFrame(tick);
    return () => {
      if (frameRef.current !== undefined) window.cancelAnimationFrame(frameRef.current);
    };
  }, [duration, value]);

  return displayValue;
}

/** 格式化动画数值，整数保持整数，小数最多保留 2 位。 */
export function formatAnimatedMagic(value: number): string {
  return String(Math.round(Number(value || 0)));
}

function registerMagicEffectAnchor(itemId: string, element: HTMLElement): void {
  const elements = anchorRegistry.get(itemId) ?? new Set<HTMLElement>();
  elements.add(element);
  anchorRegistry.set(itemId, elements);
}

function unregisterMagicEffectAnchor(itemId: string, element: HTMLElement): void {
  const elements = anchorRegistry.get(itemId);
  if (!elements) return;
  elements.delete(element);
  if (!elements.size) anchorRegistry.delete(itemId);
}

function findMagicEffectAnchor(itemId: string): HTMLElement | undefined {
  const elements = anchorRegistry.get(itemId);
  if (!elements) return undefined;
  for (const element of Array.from(elements)) {
    if (!element.isConnected) {
      elements.delete(element);
      continue;
    }
    const rect = element.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) return element;
  }
  if (!elements.size) anchorRegistry.delete(itemId);
  return undefined;
}

function readMagicAnchorIds(event: PropRuntimeEvent): string[] {
  const ids = event.targetIds?.length
    ? event.targetIds
    : [event.item?.id, event.source?.id].filter(Boolean) as string[];
  return Array.from(new Set(ids));
}

function formatMagicDelta(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(2).replace(/\.?0+$/, "");
}

const MagicAnchor = styled.div`
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
`;

const FloatingLayer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  pointer-events: none;
  overflow: hidden;
`;

const floatUp = keyframes`
  0% {
    opacity: 0;
    transform: translate(-50%, 10px) scale(.78);
  }

  16% {
    opacity: 1;
    transform: translate(-50%, -8px) scale(1.26);
  }

  72% {
    opacity: 1;
    transform: translate(-50%, -52px) scale(1.12);
  }

  100% {
    opacity: 0;
    transform: translate(-50%, -76px) scale(1.04);
  }
`;

const FloatingText = styled.div`
  position: fixed;
  color: #facc15;
  font-size: 28px;
  font-weight: 900;
  line-height: 1;
  letter-spacing: 0;
  -webkit-text-stroke: 1px rgba(15, 23, 42, .92);
  text-shadow:
    0 2px 0 rgba(15, 23, 42, .95),
    0 0 18px rgba(250, 204, 21, .88),
    0 0 8px rgba(56, 189, 248, .72);
  filter: drop-shadow(0 0 10px rgba(250, 204, 21, .68));
  animation: ${floatUp} ${MAGIC_FLOAT_DURATION_MS}ms ease-out forwards;
  will-change: transform, opacity;
`;

function setGlobalMagicGainPlaying(playing: boolean): void {
  if (magicGainEffectPlaying === playing) return;
  magicGainEffectPlaying = playing;
  if (!playing) {
    const waiters = Array.from(magicGainWaiters);
    magicGainWaiters.clear();
    waiters.forEach((resolve) => resolve());
  }
}
